import React, { useState, useEffect, useMemo, useRef } from 'react';
import * as XLSX from 'xlsx';
import './ExcelImportModal.css';
import { getServerEndpoint } from '../../utils/utils';
import { sessionFetch } from '../../utils/sessionFetch';

// Fixed headers for import table
const HEADERS = [
    'Nom',
    'Primer cognom',
    'Segon cognom',
    'Sobrenom',
    'Altura espatlles',
    'Mail'
];

function ExcelImportTab({ socket, COLLA, existingCastellers }) {
    const serverEndpoint = getServerEndpoint(COLLA);
    const fileInputRef = useRef(null);
    const [excelFile, setExcelFile] = useState(null);
    const [excelData, setExcelData] = useState([]);
    const [excelFileName, setExcelFileName] = useState('');
    const [isUploading, setIsUploading] = useState(false);
    const [history, setHistory] = useState([]);

    // Normalize and compute duplicate indices based on (nom, primer cognom, segon cognom) or sobrenom
    const normalizeText = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim().replace(/\s+/g, ' ');
    const { duplicatesSelfIndices, duplicatesExistingIndices } = useMemo(() => {
        // Build keys for each row
        const nameKeys = excelData.map(row =>
            `${normalizeText(row['Nom'] || '')}|${normalizeText(row['Primer cognom'] || '')}|${normalizeText(row['Segon cognom'] || '')}`
        );
        const surnameKeys = excelData.map(row =>
            normalizeText(row['Sobrenom'] || '')
        );
        // Count occurrences for self-duplicates
        const nameCount = {};
        nameKeys.forEach(k => { if (k) nameCount[k] = (nameCount[k] || 0) + 1; });
        const surnameCount = {};
        surnameKeys.forEach(k => { if (k) surnameCount[k] = (surnameCount[k] || 0) + 1; });
        let dupSelf = [];
        nameKeys.forEach((k, idx) => { if (k && nameCount[k] > 1) dupSelf.push(idx); });
        surnameKeys.forEach((k, idx) => { if (k && surnameCount[k] > 1) dupSelf.push(idx); });
        dupSelf = Array.from(new Set(dupSelf));
        // Prepare existing castellers sets
        const existingNameSet = new Set(
            (existingCastellers || []).map(u =>
                `${normalizeText(u.nom || '')}|${normalizeText(u['primer-cognom'] || '')}|${normalizeText(u['segon-cognom'] || '')}`
            )
        );
        const existingSurnameSet = new Set(
            (existingCastellers || []).map(u =>
                normalizeText(u.mote || '')
            )
        );
        // Identify duplicates against existing data
        let dupExisting = [];
        nameKeys.forEach((k, idx) => { if (k && existingNameSet.has(k)) dupExisting.push(idx); });
        surnameKeys.forEach((k, idx) => { if (k && existingSurnameSet.has(k)) dupExisting.push(idx); });
        dupExisting = Array.from(new Set(dupExisting));
        return { duplicatesSelfIndices: dupSelf, duplicatesExistingIndices: dupExisting };
    }, [excelData, existingCastellers]);

    // Editable grid paste handler (skips header row only if matching fixed HEADERS)
    const handlePaste = (e) => {
        e.preventDefault();
        const text = e.clipboardData.getData('text/plain');
        if (!text) return;
        const normalizeText = value => String(value || '').normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim().replace(/\s+/g, ' ');
        const lines = text.split(/\r\n|\n|\r/).filter(l => l.trim() !== '');
        // detect if first line is header matching HEADERS
        const headerCells = lines[0].split('\t');
        const headNorm = headerCells.map(normalizeText);
        const expectedNorm = HEADERS.map(normalizeText);
        const dataLines = (headNorm.length >= HEADERS.length && expectedNorm.every((h,i) => headNorm[i] === h))
            ? lines.slice(1)
            : lines;
        // map only fixed headers
        const entries = dataLines.map(line => {
            const values = line.split('\t');
            const row = {};
            HEADERS.forEach((h, i) => { row[h] = values[i] !== undefined ? values[i] : ''; });
            return row;
        });
        setHistory(prevHistory => [...prevHistory, excelData]);
        setExcelData(prevData => [...prevData, ...entries]);
        setExcelFile(null);
    };

    const handleCellChange = (rowIdx, header, value) => {
        const newData = excelData.length > 0 ? [...excelData] : [];
        while (newData.length <= rowIdx) newData.push({});
        newData[rowIdx] = { ...newData[rowIdx], [header]: value };
        setExcelData(newData);
    };

    // Delete a row
    const handleDeleteRow = (rowIdx) => {
        const newData = excelData.filter((_, i) => i !== rowIdx);
        setHistory(prevHistory => [...prevHistory, excelData]);
        setExcelData(newData);
        setExcelFile(null);
        setExcelFileName('');
    };

    // Add a new empty row
    const handleAddRow = () => {
        setExcelFile(null);
        setExcelFileName('');
        setExcelData([...excelData, {}]);
    };

    // Handle undo action to revert last paste or deletion
    const handleUndo = () => {
        setHistory(prevHistory => {
            if (prevHistory.length === 0) return prevHistory;
            const newHistory = [...prevHistory];
            const previousData = newHistory.pop();
            setExcelData(previousData);
            return newHistory;
        });
        setExcelFile(null);
        setExcelFileName('');
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return; // User cancelled file dialog
        
        console.log('ExcelImportTab: file selected', file);
        setExcelFile(file);
        setExcelFileName(file.name);
        
        const reader = new FileReader();
        reader.onload = (evt) => {
            console.log('ExcelImportTab: reader.onload', evt);
            let workbook;
            if (evt.target.result instanceof ArrayBuffer) {
                workbook = XLSX.read(new Uint8Array(evt.target.result), { type: 'array' });
            } else {
                workbook = XLSX.read(evt.target.result, { type: 'binary' });
            }
            const sheetName = workbook.SheetNames[0];
            const sheet = workbook.Sheets[sheetName];
            const rawEntries = XLSX.utils.sheet_to_json(sheet, { defval: '' });
            console.log('ExcelImportTab: entries parsed', rawEntries);
            
            // Map Excel columns to expected HEADERS
            const entries = rawEntries.map(row => {
                const mapped = {};
                HEADERS.forEach(h => {
                    // Try exact match first, then case-insensitive
                    if (row[h] !== undefined) {
                        mapped[h] = row[h];
                    } else {
                        // Try to find a matching column (case-insensitive, accent-insensitive)
                        const normalizeForMatch = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim().replace(/\s+/g, ' ');
                        const headerNorm = normalizeForMatch(h);
                        const matchingKey = Object.keys(row).find(k => normalizeForMatch(k) === headerNorm);
                        mapped[h] = matchingKey ? row[matchingKey] : '';
                    }
                });
                return mapped;
            });
            
            // Save current state to history for undo
            setHistory(prevHistory => [...prevHistory, excelData]);
            setExcelData(entries);
        };
        reader.onerror = (error) => {
            console.error('ExcelImportTab: error parsing excel file', error);
            setExcelData([]);
        };
        if (reader.readAsArrayBuffer) {
            reader.readAsArrayBuffer(file);
        } else {
            reader.readAsBinaryString(file);
        }
        
        // Reset file input so the same file can be selected again
        e.target.value = '';
    };

    const handleSubmit = () => {
        // Validate required fields
        const invalidRows = excelData.filter(row => 
            !String(row['Nom'] ?? '').trim() || !String(row['Primer cognom'] ?? '').trim()
        );
        
        if (invalidRows.length > 0) {
            alert(`${invalidRows.length} files tenen camps obligatoris buits (Nom, Primer cognom). Si us plau, completa'ls abans de continuar.`);
            return;
        }

        // Block submission if there are duplicates detected on the client
        if (duplicatesSelfIndices.length > 0 || duplicatesExistingIndices.length > 0) {
            alert(`Hi ha duplicats detectats. Si us plau, elimina o edita els ${duplicatesSelfIndices.length + duplicatesExistingIndices.length} duplicats abans de continuar.`);
            return;
        }
        
        setIsUploading(true);
        if (excelFile) {
            const reader = new FileReader();
            reader.readAsDataURL(excelFile);
            reader.onload = () => {
                socket.emit('.upload_excel', reader.result);
                if (COLLA === 'demo') {
                    alert("S'haguessin enviat emails de benvinguda a tots els usuaris de la llista. A la demo està desactivat.");
                }
            };
            reader.onerror = (error) => {
                console.error('ExcelImportTab: error reading the file', error);
                setIsUploading(false);
            };
        } else if (excelData.length > 0) {
            try {
                const ws = XLSX.utils.json_to_sheet(excelData);
                const wb = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
                const base64 = XLSX.write(wb, { bookType: 'xlsx', type: 'base64' });
                const dataUrl = 'data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,' + base64;
                socket.emit('.upload_excel', dataUrl);
                if (COLLA === 'demo') {
                    alert("S'haguessin enviat emails de benvinguda a tots els usuaris de la llista. A la demo està desactivat.");
                }
            } catch (error) {
                console.error('ExcelImportTab: error generating workbook', error);
                setIsUploading(false);
            }
        } else {
            console.warn('ExcelImportTab: no data to upload');
            setIsUploading(false);
        }
    };

    const handleClear = () => {
        setExcelData([]);
        setExcelFile(null);
        setExcelFileName('');
        setHistory([]);
    };

    useEffect(() => {
        const onExcelUploaded = () => {
            alert("S'ha penjat correctament l'Excel!");
            setIsUploading(false);
            setExcelFile(null);
            setExcelData([]);
            setExcelFileName('');
            window.location.reload();
        };
        socket.on('.excel_uploaded', onExcelUploaded);
        return () => {
            socket.off('.excel_uploaded', onExcelUploaded);
        };
    }, [socket]);

    const handleDownloadTemplate = async () => {
        const response = await sessionFetch(`${serverEndpoint}/api/download-template`);
        if (!response.ok) return;
        const url = URL.createObjectURL(await response.blob());
        const link = document.createElement('a');
        link.href = url;
        link.download = 'plantilla.xlsx';
        link.click();
        setTimeout(() => URL.revokeObjectURL(url), 10000);
    };

    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    return (
        <div className="excel-import-tab">
            <div className="excel-import-header">
                <div>
                    <h2>Importar castellers</h2>
                    <p className="import-instructions">
                        Enganxa des d'Excel o afegeix manualment les dades dels castellers. Els camps <strong>Nom</strong> i <strong>Primer cognom</strong> són obligatoris.
                    </p>
                    <p className="import-instructions">
                        El <strong>Mail</strong> s'utilitzarà per enviar una contrasenya temporal als usuaris perquè puguin iniciar sessió.
                    </p>
                </div>
                <button
                    className="undo-icon-button"
                    onClick={handleUndo}
                    disabled={isUploading || history.length === 0}
                    title="Desfer"
                    aria-label="Desfer"
                >
                    ↺ Desfer acció
                </button>
            </div>

            <div className="template-section">
                <div className="template-info">
                    <i className="fas fa-file-excel template-icon"></i>
                    <div>
                        <strong>Plantilla Excel</strong>
                        <p>Descarrega la plantilla, omple-la amb les dades dels castellers i torna-la a pujar.</p>
                    </div>
                </div>
                <div className="template-buttons">
                    <button 
                        className="template-download-button"
                        onClick={handleDownloadTemplate}
                    >
                        <i className="fas fa-download"></i> Descarregar plantilla
                    </button>
                    <button 
                        className="template-upload-button"
                        onClick={handleUploadClick}
                        disabled={isUploading}
                    >
                        <i className="fas fa-upload"></i> Pujar Excel
                    </button>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".xlsx,.xls"
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                    />
                </div>
                {excelFileName ? (
                    <div className="file-loaded-badge">
                        <i className="fas fa-check-circle"></i> {excelFileName}
                    </div>
                ) : null}
            </div>

            {excelData.length > 0 ? (
                <div className="import-badges">
                    <span className="badge badge-primary" title="Castellers a afegir">
                        Afegint {excelData.length} castellers
                    </span>
                    {duplicatesSelfIndices.length > 0 ? (
                        <span className="badge badge-warning" title="Duplicats dins l'import">
                            {duplicatesSelfIndices.length} duplicats dins
                        </span>
                    ) : null}
                    {duplicatesExistingIndices.length > 0 ? (
                        <span className="badge badge-danger" title="Coincidències amb castellers existents">
                            {duplicatesExistingIndices.length} duplicats
                        </span>
                    ) : null}
                </div>
            ) : null}

            <div 
                className="table-container" 
                onPaste={handlePaste}
                tabIndex={0}
            >
                <table className="data-table">
                    <thead>
                        <tr>
                            {HEADERS.map(h => (
                                <th key={h}>
                                    {h} {(h === 'Nom' || h === 'Primer cognom') && <span style={{ color: 'red' }}>*</span>}
                                </th>
                            ))}
                            <th>Accions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(excelData.length > 0 ? excelData : [{}]).map((row, rIdx) => (
                            <tr
                                key={rIdx}
                                style={{
                                    backgroundColor: duplicatesExistingIndices.includes(rIdx)
                                        ? '#ffdddd'
                                        : duplicatesSelfIndices.includes(rIdx)
                                        ? '#fff4e5'
                                        : 'inherit'
                                }}
                            >
                                {HEADERS.map((h, cIdx) => (
                                    <td key={cIdx}>
                                        <input
                                            type="text"
                                            value={row[h] || ''}
                                            onChange={e => handleCellChange(rIdx, h, e.target.value)}
                                            disabled={isUploading}
                                            placeholder={h}
                                            style={{
                                                borderLeft: (h === 'Nom' || h === 'Primer cognom') && !row[h] ? '3px solid #ff5757' : 'none'
                                            }}
                                            required={h === 'Nom' || h === 'Primer cognom'}
                                        />
                                    </td>
                                ))}
                                <td className="action-cell">
                                    <button 
                                        className="delete-row-button" 
                                        onClick={() => handleDeleteRow(rIdx)} 
                                        disabled={isUploading}
                                        aria-label="Eliminar fila"
                                    >
                                        ✕
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            
            <button 
                className="add-row-button" 
                onClick={handleAddRow} 
                disabled={isUploading}
            >
                + Afegir fila
            </button>
            
            <div className="action-buttons">
                <button 
                    className="confirm-button"
                    onClick={handleSubmit} 
                    disabled={isUploading || excelData.length === 0 || duplicatesSelfIndices.length > 0 || duplicatesExistingIndices.length > 0}
                >
                    {isUploading ? 'Carregant...' : 'Confirmar importació'}
                </button>
                {excelData.length > 0 ? (
                    <button 
                        className="cancel-button" 
                        onClick={handleClear} 
                        disabled={isUploading}
                    >
                        Netejar taula
                    </button>
                ) : null}
            </div>
        </div>
    );
}

export default ExcelImportTab;
